import React from 'react';
import type { SnackbarOrigin } from '@mui/material';
import { Alert, Snackbar } from '@mui/material';

import type { SnackbarState } from './useSnackbar';

export interface FeedbackSnackbarProps {
  /** Snackbar state from useSnackbar hook */
  state: SnackbarState | null;
  /** Callback when snackbar is closed */
  onClose: () => void;
  /** Auto-hide duration in milliseconds. Default: 4000 */
  autoHideDuration?: number;
  /** Position of the snackbar on screen */
  anchorOrigin?: SnackbarOrigin;
}

/**
 * A feedback snackbar component for displaying brief messages.
 * Use with the useSnackbar hook for state management.
 *
 * @example
 * const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();
 *
 * <FeedbackSnackbar state={snackbarState} onClose={closeSnackbar} />
 *
 * // Trigger snackbar
 * showSnackbar('Copied to clipboard', 'success');
 */
export const FeedbackSnackbar: React.FC<FeedbackSnackbarProps> = ({
  state,
  onClose,
  autoHideDuration = 4000,
  anchorOrigin,
}) => {
  return (
    <Snackbar open={!!state} autoHideDuration={autoHideDuration} onClose={onClose} anchorOrigin={anchorOrigin}>
      <Alert onClose={onClose} severity={state?.severity || 'info'}>
        {state?.message || ''}
      </Alert>
    </Snackbar>
  );
};

export default FeedbackSnackbar;
